import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wallet, Coins, Loader2, Crown, Check, GraduationCap, Video, Users, Zap, Sparkles, Hammer } from "lucide-react";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import Logo from "@/components/Logo";
import { BrandName } from "@/components/BrandName";
import MobileCreateButton from "@/components/MobileCreateButton";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PlanItem = ({ item }) => (
  <li className="flex items-start gap-2 text-sm text-white font-semibold">
    <Check size={16} className="text-white mt-0.5 shrink-0" />
    <span>
      {item.label}
      {item.sub && <span className="block font-extrabold leading-snug" style={{ color: item.subColor || "#c2760a" }}>{item.sub}</span>}
    </span>
  </li>
);

const PlanCard = ({ testid, listId, color, badge, price, sub, items }) => (
  <div className="rounded-2xl border-2 bg-[#eef1fb] p-5 sm:p-6 flex flex-col relative" style={{ borderColor: color }} data-testid={testid}>
    <span className="absolute top-3.5 right-3.5 inline-flex items-center gap-1 text-[10px] font-extrabold text-white rounded-full px-2 py-0.5" style={{ background: color }}>{badge}</span>
    <div className="text-center sm:text-left shrink-0 mb-4">
      <div className="text-5xl font-extrabold text-white leading-none">{price}</div>
      <div className="text-sm font-bold text-white mt-1">{sub}</div>
    </div>
    <ul className="grid grid-cols-1 gap-y-1.5 flex-1 w-full" data-testid={listId}>
      {items.map((item) => <PlanItem key={item.label} item={item} />)}
    </ul>
  </div>
);

const BONUS = [[200, 0.2], [100, 0.15], [50, 0.1], [25, 0.05]];
const bonusFor = (usd) => (BONUS.find(([min]) => usd >= min) || [0, 0])[1];

// Slim "fill your wallet once" card — one dropdown, one click.
const WalletTopUp = ({ busy, onBuy }) => {
  const [usd, setUsd] = useState(25);
  const pct = bonusFor(usd);
  const total = usd * (1 + pct);
  return (
    <div className="sms-tweed rounded-2xl border-2 border-[#5d8a52] p-5 sm:p-6" data-testid="wallet-topup-card">
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Wallet size={20} className="text-[#ffd21e]" />
            <h3 className="text-lg sm:text-xl font-extrabold text-white">Top up once. Create whenever inspiration hits.</h3>
          </div>
          <p className="text-sm text-white/75 mt-1.5 leading-relaxed">
            Skip the checkout on every video. Fill your wallet in one quick click, and each new video is <b className="text-white">one tap to create</b>: no cards, no forms, no waiting.
            Bigger wallets get <b className="text-[#ffd21e]">bonus cash on us</b>, up to <b className="text-[#ffd21e]">+20%</b>. Your balance never expires.
          </p>
        </div>
        <div className="lg:w-[340px] shrink-0 flex flex-col gap-2">
          <select
            data-testid="wallet-topup-select"
            value={usd}
            onChange={(e) => setUsd(Number(e.target.value))}
            className="w-full text-sm font-bold text-white bg-black border-2 border-[#5d8a52] rounded-full px-4 py-2.5 outline-none cursor-pointer"
          >
            {TOPUPS.map(({ usd: u, blurb }) => {
              const p = bonusFor(u);
              return <option key={u} value={u}>${u}{p ? ` + ${Math.round(p * 100)}% bonus` : ""} · {blurb}</option>;
            })}
          </select>
          <div className="text-xs font-bold text-white/80 text-center" data-testid="wallet-topup-summary">
            You get <span className="text-[#ffd21e]">${total.toFixed(2)}</span>
            {pct > 0 && <> (<span className="text-[#ffd21e]">+${(usd * pct).toFixed(2)} free</span>)</>}
            {" · "}≈ {Math.floor(total / 2.99)} Creator videos or {Math.floor(total / VIDEO_PRICE)} Generated
          </div>
          <button
            data-testid="wallet-topup-btn"
            onClick={() => onBuy(usd)}
            disabled={!!busy}
            className="w-full font-extrabold py-3 rounded-full flex items-center justify-center gap-2 text-white bg-[#1e3aaa] hover:bg-[#16307f] transition-colors disabled:opacity-60"
          >
            {busy === usd ? <Loader2 size={17} className="spin" /> : <Wallet size={16} />} Add ${usd} to my wallet
          </button>
        </div>
      </div>
    </div>
  );
};

// Face-value top-up amounts (you add $X, you get exactly $X of balance).
const TOPUPS = [
  { usd: 10, blurb: "Get rolling" },
  { usd: 25, blurb: "Most popular", popular: true },
  { usd: 50, blurb: "Best value", beast: true },
  { usd: 75, blurb: "For regulars" },
  { usd: 100, blurb: "Serious creators" },
  { usd: 200, blurb: "Go all in" },
];

// Per-item prices.
const VIDEO_PRICE = 3.99;
const ASSETS_PRICE = 2.99;
const SOCIAL_PRICE = 1.99;

// One product, one price: a full narrated video + the entire kit.
const INCLUDED = [
  { label: "Smart-Edit built in", sub: "Zero editing — we build and polish the whole video", subColor: "#8ea2ff" },
  { label: "Narrated MP4 video (15 illustrated steps)" },
  { label: "Add-on: Chat with an AI Expert", sub: "Upgrade your video with a guardrailed AI expert your viewers can ask anything about your video topic", subColor: "#6b8dff" },
  { label: "PDF manual + all step photos" },
  { label: "Keynote / PowerPoint slideshow" },
  { label: "20-question quiz" },
  { label: "Printable worksheet + answer key" },
  { label: "MP3 audiobook" },
  { label: "Amazon — Earn while they Learn", sub: "Use your Amazon Associate ID to earn Cash!" },
  { label: "Post to YouTube", sub: "Download and post your Video & Affiliate Links on Youtube!", subColor: "#cc0000" },
];

// Digital Asset Pack — the lesson kit WITHOUT a video (no narration, no audiobook, no Amazon links).
const CREATOR_INCLUDED = [
  { label: "Smart-Edit built in", sub: "Upload your story — we handle the editing", subColor: "#8ea2ff" },
  { label: "Your images, your video clips", sub: "Up to 15 slots — upload, shoot in-app, or mix photos & 10-second clips", subColor: "#8fbf82" },
  { label: "Your story, in your own words", sub: "You write every step — no AI script, no generic filler", subColor: "#ffd21e" },
  { label: "Your voice", sub: "Record each step with a 3-2-1 countdown — or pick a Male / Female AI voice", subColor: "#ff9a52" },
  { label: "Auto captions", sub: "Custom colours & fonts — fix any misheard word", subColor: "#8fbf82" },
  { label: "Trim, swap & re-record any clip" },
  { label: "2 free AI photo touch-ups per video", sub: "Extra touch-ups just $0.25 each", subColor: "#9a96ac" },
  { label: "Branded end card + Amazon affiliate links" },
  { label: "Building is FREE", sub: "You only pay when you export your video", subColor: "#ffd21e" },
];

const ASSETS_INCLUDED = [
  { label: "All 15 high-res step images" },
  { label: "Printable PDF guide" },
  { label: "Keynote / PowerPoint slideshow" },
  { label: "Printable worksheet + answer key" },
  { label: "20-question quiz" },
  { label: "One-click .zip — ready in minutes", sub: "Presentation & study-ready — no video, no fluff", subColor: "#4f7746" },
  { label: "More premium assets coming soon", sub: "We keep adding value to the pack" },
];

// Social Mode — a fast 60s portrait reel for Shorts / Reels / TikTok.
const SOCIAL_INCLUDED = [
  { label: "Smart-Edit built in", sub: "Drop in your clips — we sync, caption & cut your reel", subColor: "#8ea2ff" },
  { label: "60-second portrait reel (9:16)", sub: "Ready for Shorts, Reels & TikTok", subColor: "#e0455c" },
  { label: "Lightning-fast render", sub: "Your reel is ready in under a minute" },
  { label: "Upload your own photos — or snap live with the camera" },
  { label: "AI images too — describe it, we draw it" },
  { label: "Voice-over studio", sub: "Record 60 seconds of audio — or use an AI voice — right in the app" },
  { label: "Import your own music (MP3)" },
  { label: "On-screen captions + AI script & caption helpers" },
  { label: "Branded end card", sub: "Your logo, QR, domain — or keep it clean", subColor: "#4f7746" },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) {
      setVerifying(false);
      toast.error("Payment status is taking a while. Refresh in a moment.");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        setVerifying(false);
        toast.success(data.kind === "moviestar" ? "MovieStar Mode unlocked for 30 days 🎬" : "Credits added — happy making! 🎉");
        const pending = localStorage.getItem("sms_pending_goal");
        if (data.kind !== "moviestar" && pending) {
          navigate("/", { state: { restoreGoal: true } });
        } else {
          setParams({});
        }
        return;
      }
      if (data.status === "expired") {
        setVerifying(false);
        toast.error("Checkout expired. Please try again.");
        setParams({});
        return;
      }
    } catch {}
    setTimeout(() => poll(sessionId, attempts + 1), 2000);
  }, [refresh, setParams, navigate]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setVerifying(true); poll(sid); }
  }, [params, poll]);

  const buy = async (amountUsd) => {
    setBusy(amountUsd);
    try {
      const { data } = await api.post("/payments/checkout-custom", { amount_usd: amountUsd, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-black">
        <Loader2 size={30} className="spin text-[#5d8a52]" />
        <p className="font-medium">Confirming your payment…</p>
      </div>
    );
  }

  return (
    <div className="sms-plans-dark max-w-7xl mx-auto px-4 pt-3 pb-16">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2 min-w-0 text-white" data-testid="plans-page-title">
          <BrandName dark /> <span className="inline-flex items-center gap-2 whitespace-nowrap sms-outline-dark">PLANS <Coins size={22} className="text-white shrink-0" /></span>
        </h1>
        <MobileCreateButton />
        {user && (
          <div className="hidden sm:flex items-center gap-2">
            <span data-testid="pricing-balance" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#5d8a52] bg-[#eef3ea] px-3 py-1.5 rounded-full">
              {user.unlimited ? <><Crown size={15} /> Unlimited</> : <><Wallet size={15} /> ${(((user.credits ?? 0)) / 100).toFixed(2)}</>}
            </span>
            {!user.unlimited && (
              <button
                data-testid="pricing-buy-credits-btn"
                onClick={() => setShowBuyCredits(true)}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-white px-3.5 py-1.5 rounded-full transition-colors"
                style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}
              >
                <Wallet size={15} /> Add Funds
              </button>
            )}
          </div>
        )}
      </div>
        {user && (
          <div className="sm:hidden flex items-center justify-center gap-2 mb-3">
            <span data-testid="pricing-balance-m" className="inline-flex items-center gap-1.5 text-sm font-bold text-[#5d8a52] bg-[#eef3ea] px-3 py-1.5 rounded-full">
              {user.unlimited ? <><Crown size={15} /> Unlimited</> : <><Wallet size={15} /> ${(((user.credits ?? 0)) / 100).toFixed(2)}</>}
            </span>
            {!user.unlimited && (
              <button
                data-testid="pricing-buy-credits-btn-m"
                onClick={() => setShowBuyCredits(true)}
                className="inline-flex items-center gap-1.5 text-sm font-bold text-white px-3.5 py-1.5 rounded-full transition-colors"
                style={{ background: "linear-gradient(135deg,#5d8a52,#3f6238)" }}
              >
                <Wallet size={15} /> Add Funds
              </button>
            )}
          </div>
        )}

      <div className="text-center mb-8 mt-2 sms-fade-up">
        <img src="/vittyyo-icon.svg?v=3" alt="vittyyo" data-testid="pricing-app-icon" className="w-14 h-14 mx-auto mb-4" draggable={false} />
        <h1 className="text-3xl md:text-4xl font-bold sms-outline-dark">Add Funds — Create Your Content</h1>
        <p className="text-white mt-2 text-lg">Your wallet is simple cash. Pick how you create: <b>Generated $3.99</b> (AI builds it all), <b>Creator $2.99</b> (your images, your story) or <b>Social $1.99</b> (a 60-second reel). No tiers, no subscriptions.<span className="block mt-2 font-bold">Balance never expires.</span></p>
      </div>


      {/* Plans */}
      <div className="sms-card p-5 mb-8 border-2 border-[#5d8a52]" data-testid="how-credits-work">
        <div className="flex flex-col items-center gap-2 mb-5" data-testid="plans-header">
          <img src="/vittyyo-wordmark.svg?v=4" alt="vittyyo" data-testid="plans-wordmark" className="h-12 md:h-16 w-auto" draggable={false} />
          <div className="font-extrabold text-3xl md:text-4xl text-[#171426]">Plans</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch" data-testid="plan-cards">
          <PlanCard testid="video-plan-card" listId="included-list" color="#1e3aaa" badge={<><Sparkles size={11} /> Generated</>} price="$3.99" sub="Video Generate Mode · AI builds it all, everything included" items={INCLUDED} />
          <PlanCard testid="creator-plan-card" listId="creator-included-list" color="#5d8a52" badge={<><Hammer size={11} /> Creator</>} price="$2.99" sub="Video Creator Mode · your images, your story, your voice" items={CREATOR_INCLUDED} />
          <PlanCard testid="social-plan-card" listId="social-included-list" color="#e0455c" badge={<><Zap size={11} /> Social</>} price="$1.99" sub="Social Video Mode · a 60s reel for Shorts, Reels & TikTok" items={SOCIAL_INCLUDED} />
        </div>

        {/* ASSET PACK — $2.99 (no video), full-width */}
        <div className="mt-4 rounded-2xl border-2 border-[#ffd21e] bg-[#eef1fb] p-5 sm:p-6 relative" data-testid="asset-pack-plan-card">
          <span className="absolute top-3.5 right-3.5 inline-flex items-center gap-1 text-[10px] font-extrabold text-black bg-[#ffd21e] rounded-full px-2 py-0.5"><GraduationCap size={11} /> No Video</span>
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            <div className="md:w-72 shrink-0">
              <div className="text-xl font-extrabold text-white leading-tight">Don't need a video?</div>
              <div className="text-xl font-extrabold text-[#ffd21e] leading-tight">Create your Asset Pack now.</div>
              <div className="text-5xl font-extrabold text-white leading-none mt-3">$2.99</div>
              <div className="text-sm font-bold text-white mt-1">Great for teachers, trainers &amp; coaches</div>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 flex-1" data-testid="asset-pack-included-list">
              {[...ASSETS_INCLUDED, { label: "Sell Your Own Digital Assets", sub: "Coming soon", subColor: "#ffd21e" }].map((item) => <PlanItem key={item.label} item={item} />)}
            </ul>
          </div>
        </div>
        <p className="text-sm text-black mt-3">
          Flat prices — no tiers, no subscriptions. Your balance is charged only the moment your video (or asset pack) is created. Browsing, drafting and building never cost anything. <span className="text-black">(Want the same video re-voiced in another language? Just $0.50 extra.)</span>
        </p>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {user && !user.unlimited ? (
            <button
              data-testid="how-credits-buy-btn"
              onClick={() => setShowBuyCredits(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-extrabold text-white px-5 py-2.5 rounded-full transition-transform hover:-translate-y-0.5"
              style={{ background: "#1e3aaa", boxShadow: "0 8px 18px -6px rgba(30,58,170,.55)" }}
            >
              <Wallet size={17} /> Add Funds — choose your amount
            </button>
          ) : <span />}
          <button
            data-testid="back-to-community-btn"
            onClick={() => navigate("/")}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-extrabold text-white px-5 py-2.5 rounded-full transition-transform hover:-translate-y-0.5"
            style={{ background: "#1e3aaa", boxShadow: "0 8px 18px -6px rgba(30,58,170,.55)" }}
          >
            <Users size={17} /> Back to Community
          </button>
        </div>
      </div>

      <WalletTopUp busy={busy} onBuy={buy} />

      <p className="text-center text-base text-[#171426] mt-6">
        Secure one-time checkout by Stripe. Prices in USD. Funds are added instantly and never expire.
      </p>

      <BuyCreditsModal open={showBuyCredits} onClose={() => setShowBuyCredits(false)} />
    </div>
  );
}
